import { ColumnModel, EditSettingsModel, Filter } from '@syncfusion/ej2-angular-grids';
import { Query } from '@syncfusion/ej2-data';
import { SplitterComponent } from '@syncfusion/ej2-angular-layouts';

import { FieldEditChangedArgs, FieldWidthEnum } from '../../../cer/cer-data/cer-data-types';
import { UiActionTypeEnum, UiCommand, UiCommandEvent, UiCommandSourceEnum } from '@ui/cer-ui-types';
import { CerFormPanesSettings, CerFormPanesSettingsUtil } from '@form/cer-form-panes/cer-form-panes.service';
import { CerMetaUserView } from '@form/cer-form-persistence.service';

import { OnHandCellInfo } from '@app/warehousing/forms/warehouse-onhand-status.form';
import { CerGridComponent } from 'src/cer/cer-grid/cer-grid.component';
import { CerFormAdapter } from '@cer/cer-form/cer-form-adapter';
import { CerFormViewSet } from '@cer/cer-form/cer-form-view-set';
import { CerFormView } from '@cer/cer-form/cer-form-view';
import { CerAppDataNotificationService } from 'src/cer-app/cer-app-notifications/cer-app-data-notification.service';

const uiCmdSaveFilter: UiCommand = { id: 'uiCmdSaveFilter', text: 'Gem opsætning', tooltipText: 'Brug nuværende filter fra linjerne på denne liste', target: ".e-content", iconCss: "e-menu-icon e-icons-org e-changes-accept" };
const uiCmdClearFilter: UiCommand = { id: 'uiCmdClearFilter', text: 'Nulstil opsætning', tooltipText: 'Brug nuværende filter fra linjerne på denne liste', target: ".e-content", iconCss: "e-menu-icon e-icons-org e-changes-reject" };

export class ProductListForm extends CerFormAdapter {

  private productListView: CerFormView;

  public override viewSetInit(viewSet: CerFormViewSet) {
    viewSet.panes = CerFormPanesSettingsUtil.masterDetail('horizontal', true, 70);

    this.productListView = this.productListViewGet();
    viewSet.viewAdd(this.productListView);

    const viewProducts = this.productViewGet();
    viewSet.viewAdd(viewProducts, this.productListView);

    // Only add notification setup pane, if routed directly
    if (this.routeOptions?.navigation == null) {
      const viewNotification = this.viewNotificationGet(this.productListView.viewMetadata.dataApiName);
      viewSet.viewAdd(viewNotification, this.productListView);
    }

    // When notification id query param changes
    if (this.routeOptions.queryParamMap) {
      this.context.subscriptionManager.add(
        this.routeOptions.queryParamMap.subscribe(map => {
          if (map) {
            var s: string = map.get('notificationId');
            var id: number = (s != null) ? Number(s) : null;
            if (id != null) {
              //viewSet.panes.right.size = "85%";
              this.setNotificationIdFilter(this.productListView, id);
            }
          }
        })
      );
    }
  }

  // ** Product list **
  private productListViewGet(): CerFormView {
    return new CerFormView({
      viewMetadata: {
        name: 'lister', dataApiName: 'ProductList', text: 'Titellister', textSingular: 'Titelliste',
        primaryKey: 'num', baseFields: ['id', 'num', 'name', 'history'], titleFields: ['num', 'name']
      },
      contextMenuCommands: [uiCmdSaveFilter, uiCmdClearFilter],
      toolbarCommands: [uiCmdSaveFilter, uiCmdClearFilter],
      onCommandEvent: (e => this.onProductListCommand(e)),
      fieldMetadata: [
        { name: 'num', text: 'Liste', tooltipText: 'Koden på listen', allowEdit: false, allowEditOnAdd: true, required: true, validationMaxLen: 20, orderBy: 'Ascending', orderByPriority: 2 },
        { name: 'name', text: 'Navn', required: true },
        { name: 'lineType', text: 'Filtereret?', format: 'CheckBox' },
        { name: 'lineFilter', text: 'Filter formel', visible: false /*, defaultValue: this.defaultGridSettings*/ },
        { name: 'lineUniquePriority', text: 'Rækkefølge', format: 'Integer', orderBy: 'Ascending', orderByPriority: 1, defaultValue: 100 },
        { name: 'lineUnique', text: 'Notificer med andre lister med samme rækkefølge?', tooltipText: 'Liste medtages på notifikationer for lister der har samme rækkefølge', format: 'CheckBox', visible: true, defaultValue: false }
      ]
    });
  }

  private productListGrid: CerGridComponent;
  private onProductListCommand(event: UiCommandEvent) {
    if (this.productListGrid == null) {
      if (!event.appComponent) {
        return;
      }
      else {
        this.productListGrid = <CerGridComponent>event.appComponent;
      }
    }

    switch (event.actionType) {
      case UiActionTypeEnum.Add:
        this.productGridLineFilterDefaultSet();
        break;
      case UiActionTypeEnum.RowSelect:
        var rowData = event.rowData;
        this.enableRowItems(rowData);
        break;
      case UiActionTypeEnum.RowDeselect:
        this.enableRowItems(null);
        break;
      case UiActionTypeEnum.PersistenceApply:
        if (event.source == UiCommandSourceEnum.ActionBegin) {
          this.onViewApply(event.args)
        }
        break;
    }
    switch (event.commandId) {
      case uiCmdSaveFilter.id:
        this.ProductGridFilterSave(event.rowData, false);
        break;
      case uiCmdClearFilter.id:
        this.ProductGridFilterSave(event.rowData, true);
        break;
    }
  }

  public setNotificationIdFilter(view: CerFormView, id: number) {
    if (id != null) {
      var param: string = '$filterDataNotification=' + id
      view.viewMetadata.dataApiParams = [param];
      view.fieldMetadata.filter(f => f.name != 'name').forEach(f => f.visible = false);
      const productGrid: CerGridComponent = this.productGrid;
      if (productGrid?.grid?.query) {
        let query: Query = productGrid.grid.query;
        if (query != null) {
          productGrid.grid.query.params = [];
          let parms: string[] = param?.split('=');
          if (parms.length == 2) {
            productGrid.grid.query.addParams(parms[0], parms.length > 1 ? parms[1] : 'true');
          }
        }
        productGrid.grid.columns.forEach((c: any) => {
          if (c.field != 'name') {
            c.visible = false;
          }
        });
        productGrid.research();
        var panes: CerFormPanesSettings = this.context.form?.panesService?.panesGet();
        if (panes?.right?.splitter) {
          var splitter: SplitterComponent = panes.right.splitter;
          if (splitter.element?.childNodes?.length > 0) {
            var ele: HTMLElement = <HTMLElement>splitter.element.childNodes[0];
            ele.style.flexBasis = '15%';
          }
        }
      }
    }
  }

  public onViewApply(view: CerMetaUserView) {
    var hasId: boolean = false;
    if (view?.type == 'design' || view?.data) {
      if (view.data) {
        var params: string[] = view.data;
        params.forEach(p => {
          var segment: string[] = p.split("=");
          if (segment.length == 2) {
            var id: number = Number(segment[1])
            if (id) {
              hasId = true;
              this.setNotificationIdFilter(this.productListView, id);
            }
          }
        });
      }
    }
    if (!hasId) {
      //this.view
    }
  }

  // ** Notification setup **
  private viewNotificationGet(defaulttableName: string): CerFormView {
    return new CerFormView({
      viewMetadata: CerAppDataNotificationService.viewMetadataGet([defaulttableName]),
      toolbarCommands: CerAppDataNotificationService.toolbarCommands,
      onCommandEvent: (e => this.onNotificationCommand(e)),
      fieldMetadata: CerAppDataNotificationService.fieldMetadataGet(defaulttableName)
    });
  }

  private onNotificationCommand(event: UiCommandEvent): void {
    CerAppDataNotificationService.onCommand(event, this.context.injector);
  }

  // ** Products **
  private productViewGet(): CerFormView {
    const productView = new CerFormView({
      viewMetadata: {
        name: 'titler', dataApiName: 'ProductListLineOnHand', text: 'Titler', textSingular: 'Titel',
        primaryKey: 'id', parentPrimaryKey: 'num', parentForeignKey: 'productList', baseFields: ['id', 'history'], titleFields: ['product', 'name'],
        navigations: [
          { link: 'navMenu', title: 'Nøgletal titel', paneTarget: 'bottom', foreignKey: { parentEntity: 'RefDataProductStat', parentColumn: 'productNum', column: 'product' } },
          { link: 'navMenu', title: 'Nøgletal pr. art', paneTarget: 'bottom', foreignKey: { parentEntity: 'RefDataProductStatCategory', parentColumn: 'productNum', column: 'product' } },
          { link: 'navMenu', title: 'Nøgletal perioder', paneTarget: 'bottom', foreignKey: { parentEntity: 'RefDataProductStatPeriod', parentColumn: 'productNum', column: 'product' } },
        ]
      },
      onCommandEvent: (e => this.onProductCommand(e)),
      fieldMetadata: [
        {
          name: 'productList', text: 'Liste', allowEdit: false, allowEditOnAdd: false, required: true, visible: false,
          foreignKeyTableName: 'ProductList', foreignKeyField: 'num', foreignKeyValue: 'num',
          foreignKeyAllowFiltering: true, foreignKeyColumns: ['num', 'name'], foreignKeyTitles: ['Kode', 'Listenavn'],
          foreignKeyColumnsWidth: ['120px', '380px'], popupWidth: '500px', popupHeight: '400px',
          foreignKeySearchFields: ['num', 'name'], foreignKeySearchOperators: ['contains', 'contains']
        },
        {
          name: 'product', text: 'ISBN', tooltipText: 'ISBN på titel i listen', widthType: FieldWidthEnum.S,
          allowEdit: false, allowEditOnAdd: true, required: true, visibleAdd: true, visibleEdit: true,
          orderBy: 'Ascending', orderByPriority: 2,
          foreignKeyType: 'product',
          onEditChanged: (args) => { this.editProduct(args); }
        },
        { name: 'name', text: 'Titel', required: true, visibleAdd: true, visibleEdit: true },
        { name: 'qty', text: 'Antal', format: 'Number', required: true, defaultValue: 1, visible: false },
        { name: 'fromDate', text: 'Fra dato', format: 'DateShort', visible: false },
        { name: 'toDate', text: 'Til dato', format: 'DateShort', visible: false },
        { name: 'contractNum', text: 'Projekt', widthType: FieldWidthEnum.XXS },
        { name: 'responsible', text: 'Redaktør', widthType: FieldWidthEnum.XXS },
        { name: 'group', text: 'Gruppe', widthType: FieldWidthEnum.XXS },
        { name: 'contractType', text: 'Kontrakttype', widthType: FieldWidthEnum.XXS, visible: false },
        { name: 'settlementType', text: 'Afregningstype', widthType: FieldWidthEnum.XXS, visible: false },
        { name: 'name2', text: 'Undertitel', visible: false },
        { name: 'seriesId', text: 'Serie', visible: false },
        { name: 'bookstoreClassification', text: 'Varegruppe', visible: false, foreignKeyType: 'productGroup' },
        { name: 'categories', text: 'Kategorier', visible: false },
        { name: 'physicalQty', text: 'Fysisk antal', format: 'Number', visible: false },
        { name: 'reservedQty', text: 'Reserveret antal', format: 'Number' },
        { name: 'remainQty', text: 'Resterende antal', format: 'Number', onQueryCellInfo: OnHandCellInfo.availableQty, visible: false },
        { name: 'unreservedQty', text: 'Ej reserveret antal', format: 'Number', visible: false },
        { name: 'availableQty', text: 'Disponibelt antal', format: 'Number', orderBy: 'Ascending', orderByPriority: 1, onQueryCellInfo: OnHandCellInfo.availableQty },
        { name: 'updated', text: 'Opdateret', format: 'DateShort', visible: false },
        { name: 'publishedDate', text: 'Udgivet dato', tooltipText: 'Udgivet dato fra Bogportalen', format: 'DateShort', orderBy: 'Ascending', orderByPriority: 2, onQueryCellInfo: OnHandCellInfo.publishedDate },
        { name: 'publishedDays', text: 'Udgivet dage', tooltipText: 'Antal dage udgivet fra Bogportalen', format: 'Integer', visible: false, onQueryCellInfo: OnHandCellInfo.publishedDate },
        { name: 'published', text: 'Er udgivet', tooltipText: 'Udgivet på Bogportalen', format: 'CheckBox', onQueryCellInfo: OnHandCellInfo.published },
        { name: 'buyable', text: 'Lagerføres', tooltipText: 'Kan købes på Bogportalen eller er på lager', format: 'CheckBox', onQueryCellInfo: OnHandCellInfo.buyable },
        { name: 'buyableTrade', text: 'Kan købes', tooltipText: 'Kan købes på Bogportalen af boghandlere', format: 'CheckBox', onQueryCellInfo: OnHandCellInfo.buyableTrade, visible: false },
        { name: 'salesPrice', text: 'Salgspris', format: 'Amount', visible: false },
        { name: 'apologyCode', text: 'Undskyldningskode', format: 'NumberLeft', widthType: FieldWidthEnum.XS, visible: false, onQueryCellInfo: OnHandCellInfo.apologyCode },
        { name: 'printer', text: 'Trykkeri', visible: false },
        { name: 'minimumInventory', text: 'Min. lager', format: 'Integer', widthType: FieldWidthEnum.XS, onQueryCellInfo: OnHandCellInfo.sourcingMinOnhandQty },
        { name: 'minimumInventoryPct', text: '% min. lager', format: 'IntegerPct', widthType: FieldWidthEnum.XS, onQueryCellInfo: OnHandCellInfo.sourcingMinOnhandQty },
        { name: 'warehouseItemNum', text: 'Lager varenr.', visible: false },
        { name: 'originalTitle', text: 'Orig. titel', visible: false },
        { name: 'originalLanguage', text: 'Orig. sprog', visible: false },
        { name: 'authors', text: 'Forfatter' },
        { name: 'printPublishedDate', text: 'Udgivet bogportal print', format: 'DateShort', visible: false },
        { name: 'availableForPurchaseDate', text: 'Tilgængelig bogportal køb', format: 'DateShort', visible: false },
        { name: 'statusCode', text: 'Status', visible: false },
        { name: 'productListPrimary', text: 'Primær liste', allowEdit: false, allowEditOnAdd: false },
        { name: 'productListPrimaryPriority', text: 'Primær liste prioritet', format: 'Integer', allowEdit: false, allowEditOnAdd: false, visible: false },
        { name: 'modifiedJobId', text: 'Rest opdateret job', format: 'Number', visible: false }
      ],
      fieldGroupMetadata: [
        { idx: 1, tabIdx: 0, name: 'identification', text: 'Identifikation', fields: ['id', 'productList', 'product', 'name'], width: 240 },
        { idx: 2, tabIdx: 0, name: 'period', text: 'Periode aktiv', fields: ['fromDate', 'toDate'] },
      ]
    });
    productView.toolbarType = "full";
    productView.listPaging = "pager";

    var disableFields: boolean = false;
    productView.fieldMetadata.forEach(f => {
      if (f.name == 'contractNum') {
        disableFields = true;
      }
      if (disableFields) {
        // Disable remaining fields 
        f.allowEdit = false;
        f.allowEditOnAdd = false;
        f.visibleEdit = false;
        f.visibleAdd = false;
      }
    });
    return productView;
  }

  private productGrid: CerGridComponent;
  private onProductCommand(event: UiCommandEvent) {
    if (this.productGrid == null) {
      if (!event.appComponent) {
        return;
      }
      else {
        this.productGrid = <CerGridComponent>event.appComponent;
      }
    }

    if (event.source != 'actionComplete') {
      return;
    }

    switch (event.actionType) {
      case 'refreshFromParent':
        this.enableRowItems(event.args.row);
        this.productGridRefreshFromParent(event.args.row);
        break;
      case 'created':
        this.productGrid.grid.allowPaging = true;
        break;
    }
  }

  private editProduct(args: FieldEditChangedArgs) {
    var product: any = args.itemData;
    if (product) {
      var nameValue: string = product.name;
      var e = args.editService;
      var nameInput = e.fieldInput('name');
      if (nameInput && (nameValue?.length > 0)) {
        e.inputValueSet(nameInput, nameValue);
      }
    }
  }

  private productGridLineFilterDefaultSet() {
    var col: ColumnModel = this.productListGrid?.grid?.getColumnByField('lineFilter');
    if (col && this.productGrid) {
      col.defaultValue = JSON.stringify(this.productGrid.getPersistence());
    }
  }

  private productGridRefreshFromParent(parentData: any) {
    if (parentData) {
      this.productGridPersistenceApply(parentData);
    }
  }

  private productGridDefaultSettings: any = null;
  private productGridPersistenceApply(parentData: any) {
    var firstApply: boolean = this.productGridDefaultSettings == null;
    if (firstApply) {
      this.productGridDefaultSettings = this.productGrid.getPersistence();
    }

    var rowHasFilter: boolean = parentData.lineFilter?.length > 0;
    var applyFilter: boolean = rowHasFilter || !firstApply;
    if (parentData) {
      firstApply = false;
    }
    if (!applyFilter) {
      return;
    }
    var settings = rowHasFilter ? JSON.parse(parentData?.lineFilter) : this.productGridDefaultSettings;
    this.detailGridPersistencePrepare(settings);
    var view: CerMetaUserView = { type: 'custom', name: '', isPrimary: false, isDefault: false, formPersistenceData: settings };
    if (this.productGrid.applyPersistence(view, settings)) {
      this.productGrid.applyPersistenceComplete(view);
    }
  }

  private detailGridPersistencePrepare(settings: any) {
    var m: Filter = this.productGrid.grid.filterModule;
    var mAny: any = m;
    m.actualData = m.actualData ?? [];
    mAny.currentFilterObject = mAny.currentFilterObject ?? {};
    mAny.checkboxPrevFilterObject = mAny.checkboxPrevFilterObject ?? {};

    settings.grid = settings.grid ?? {};
    if (!settings.grid.filterSettings) {
      settings.grid.filterSettings = {
        propName: 'filterSettings',
        ignoreAccents: false,
        filterType: 'Excel'
      };
    }
    settings.grid.filterSettings.columns = settings.grid.filterSettings.columns ?? [];

    /*if (settings.grid.filterSettings.columns.length == 0) {
        var prop : any PredicateModel = {
            propName: 'columns',
            field: 'id',
            ignoreAccent: false,
            matchCase: true,
            operator: 'notequal',
            value: -1,
            type: 'number',
        }
        settings.grid.filterSettings.columns.push(prop);
    }*/

    /*var props : any[] = settings.grid.filterSettings.columns.filter((c : any) => c.field == 'id');
    if (props.length > 0) {
        props[0].value = -1;
        props[0].operator = 'notequal';
    }*/
  }

  private isFiltered(rowData: any): boolean {
    return rowData != null ? rowData.lineType == 1 : false;
  }

  private enableRowItems(rowData: any) {
    if (!this.productGrid?.grid) {
      return;
    }
    this.productGrid.filterService.filterColumnClassRemove();
    var isFilter: boolean = this.isFiltered(rowData);
    var settings: EditSettingsModel = this.productGrid.grid.editSettings;
    var allowEdit: boolean = !isFilter;
    if (rowData && allowEdit != settings.allowAdding) {
      settings.allowAdding = allowEdit;
      settings.allowEditing = allowEdit;
      settings.allowDeleting = allowEdit;
      //        this.grid.commandService.toolbarCRUDInit();
    }
  }

  private ProductGridFilterSave(rowData: any, clear: boolean) {
    if (rowData) {
      if (rowData != null) {
        var txt: string = (clear ? "Nulstil" : "Gem") + " filter/opsætning for denne titel liste?"

        this.context.ui.confirm(txt).then((action) => {
          if (action == 'ok') {
            rowData.lineFilter = JSON.stringify(clear ? this.productGridDefaultSettings : this.productGrid?.getPersistence());
            rowData.uuid = null;
            this.productListGrid?.rowSelectedKeep();
            this.productListGrid?.grid?.updateRow(rowData.id, rowData);
          }
        })
      }
      else {
        this.context.ui.info("Kan kun gemme filter for markerede titel lister");
      }
    }
  }
}
